import { useState, useEffect, useCallback } from 'react';
import { api } from '../utils/api';
import {
  Shield,
  RefreshCw,
  Plus,
  Trash2,
  Edit,
  XCircle,
  AlertCircle,
  Check,
  Search,
  Replace,
} from 'lucide-react';

interface NgKeyword {
  id: string;
  keyword: string;
  description?: string;
  isActive: boolean;
  createdAt: string;
}

interface ReplacementRule {
  id: string;
  searchText: string;
  replaceText: string;
  description?: string;
  isRegex: boolean;
  isActive: boolean;
  createdAt: string;
}

type TabType = 'ng-keywords' | 'replacement-rules' | 'test';

export default function ProductFiltersPage() {
  const [activeTab, setActiveTab] = useState<TabType>('ng-keywords');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // NGキーワード
  const [ngKeywords, setNgKeywords] = useState<NgKeyword[]>([]);
  const [showNgModal, setShowNgModal] = useState(false);
  const [editingNg, setEditingNg] = useState<NgKeyword | null>(null);
  const [newNgKeyword, setNewNgKeyword] = useState({ keyword: '', description: '', isActive: true });

  // 文言置換ルール
  const [replacementRules, setReplacementRules] = useState<ReplacementRule[]>([]);
  const [showRuleModal, setShowRuleModal] = useState(false);
  const [editingRule, setEditingRule] = useState<ReplacementRule | null>(null);
  const [newRule, setNewRule] = useState({
    searchText: '',
    replaceText: '',
    description: '',
    isRegex: false,
    isActive: true,
  });

  // テスト
  const [testText, setTestText] = useState('');
  const [testResult, setTestResult] = useState<any>(null);

  // NGキーワード取得
  const fetchNgKeywords = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/product-filters/ng-keywords');
      const data = await res.json();
      setNgKeywords(data.keywords || []);
    } catch (err) {
      setError('NGキーワード取得に失敗しました');
    } finally {
      setLoading(false);
    }
  }, []);

  // NGキーワード登録・更新
  const saveNgKeyword = async () => {
    if (!newNgKeyword.keyword) {
      setError('キーワードを入力してください');
      return;
    }

    setLoading(true);
    setError(null);
    try {
      if (editingNg) {
        await api.put(`/api/product-filters/ng-keywords/${editingNg.id}`, newNgKeyword);
      } else {
        await api.post('/api/product-filters/ng-keywords', newNgKeyword);
      }
      setShowNgModal(false);
      setEditingNg(null);
      setNewNgKeyword({ keyword: '', description: '', isActive: true });
      await fetchNgKeywords();
    } catch (err) {
      setError('NGキーワードの保存に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  // NGキーワード削除
  const deleteNgKeyword = async (id: string) => {
    if (!confirm('このNGキーワードを削除しますか？')) return;
    try {
      await api.delete(`/api/product-filters/ng-keywords/${id}`);
      await fetchNgKeywords();
    } catch (err) {
      setError('NGキーワードの削除に失敗しました');
    }
  };

  // 置換ルール取得
  const fetchReplacementRules = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/product-filters/replacement-rules');
      const data = await res.json();
      setReplacementRules(data.rules || []);
    } catch (err) {
      setError('置換ルール取得に失敗しました');
    } finally {
      setLoading(false);
    }
  }, []);

  // 置換ルール登録・更新
  const saveReplacementRule = async () => {
    if (!newRule.searchText) {
      setError('検索文字列を入力してください');
      return;
    }

    setLoading(true);
    setError(null);
    try {
      if (editingRule) {
        await api.put(`/api/product-filters/replacement-rules/${editingRule.id}`, newRule);
      } else {
        await api.post('/api/product-filters/replacement-rules', newRule);
      }
      setShowRuleModal(false);
      setEditingRule(null);
      setNewRule({ searchText: '', replaceText: '', description: '', isRegex: false, isActive: true });
      await fetchReplacementRules();
    } catch (err) {
      setError('置換ルールの保存に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  // 置換ルール削除
  const deleteReplacementRule = async (id: string) => {
    if (!confirm('この置換ルールを削除しますか？')) return;
    try {
      await api.delete(`/api/product-filters/replacement-rules/${id}`);
      await fetchReplacementRules();
    } catch (err) {
      setError('置換ルールの削除に失敗しました');
    }
  };

  // テスト実行
  const runTest = async () => {
    if (!testText) {
      setError('テスト用のテキストを入力してください');
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const res = await api.post('/api/product-filters/apply', {
        products: [{
          asin: 'TEST',
          title: testText,
          description: '',
        }],
      });
      const data = await res.json();
      setTestResult(data.products[0]);
    } catch (err) {
      setError('テスト実行に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'ng-keywords') {
      fetchNgKeywords();
    } else if (activeTab === 'replacement-rules') {
      fetchReplacementRules();
    }
  }, [activeTab, fetchNgKeywords, fetchReplacementRules]);

  return (
    <div className="p-6 space-y-6">
      {/* ヘッダー */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">商品フィルタ・文言管理</h1>
          <p className="text-sm text-gray-500 mt-1">
            NGキーワードと文言置換ルールを設定して、商品情報を自動整形
          </p>
        </div>
      </div>

      {/* エラー表示 */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-2">
          <AlertCircle className="text-red-500" size={20} />
          <span className="text-red-700">{error}</span>
          <button onClick={() => setError(null)} className="ml-auto text-red-500 hover:text-red-700">
            <XCircle size={20} />
          </button>
        </div>
      )}

      {/* タブ */}
      <div className="border-b border-gray-200">
        <nav className="flex gap-8">
          <button
            onClick={() => setActiveTab('ng-keywords')}
            className={`py-3 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'ng-keywords'
                ? 'border-red-500 text-red-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <span className="flex items-center gap-2">
              <Shield size={18} />
              NGキーワード
              {ngKeywords.filter(k => k.isActive).length > 0 && (
                <span className="bg-red-100 text-red-700 text-xs px-2 py-0.5 rounded-full">
                  {ngKeywords.filter(k => k.isActive).length}
                </span>
              )}
            </span>
          </button>
          <button
            onClick={() => setActiveTab('replacement-rules')}
            className={`py-3 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'replacement-rules'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <span className="flex items-center gap-2">
              <Replace size={18} />
              文言置換ルール
              {replacementRules.filter(r => r.isActive).length > 0 && (
                <span className="bg-blue-100 text-blue-700 text-xs px-2 py-0.5 rounded-full">
                  {replacementRules.filter(r => r.isActive).length}
                </span>
              )}
            </span>
          </button>
          <button
            onClick={() => setActiveTab('test')}
            className={`py-3 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'test'
                ? 'border-green-500 text-green-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <span className="flex items-center gap-2">
              <Search size={18} />
              フィルタテスト
            </span>
          </button>
        </nav>
      </div>

      {/* NGキーワードタブ */}
      {activeTab === 'ng-keywords' && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <p className="text-sm text-gray-600">
              登録したキーワードを含む商品は自動的に除外されます
            </p>
            <button
              onClick={() => {
                setEditingNg(null);
                setNewNgKeyword({ keyword: '', description: '', isActive: true });
                setShowNgModal(true);
              }}
              className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
            >
              <Plus size={16} />
              NGキーワード追加
            </button>
          </div>

          <div className="bg-white rounded-lg shadow overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">キーワード</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">説明</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">ステータス</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">登録日</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">操作</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td colSpan={5} className="px-4 py-8 text-center">
                      <RefreshCw size={24} className="animate-spin mx-auto mb-2" />
                      読み込み中...
                    </td>
                  </tr>
                ) : ngKeywords.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-4 py-8 text-center text-gray-500">
                      NGキーワードが登録されていません
                    </td>
                  </tr>
                ) : (
                  ngKeywords.map(keyword => (
                    <tr key={keyword.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <span className="font-medium text-red-600">{keyword.keyword}</span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {keyword.description || '-'}
                      </td>
                      <td className="px-4 py-3">
                        {keyword.isActive ? (
                          <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs">
                            <Check size={12} />
                            有効
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-600 rounded-full text-xs">
                            <XCircle size={12} />
                            無効
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500">
                        {new Date(keyword.createdAt).toLocaleDateString('ja-JP')}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => {
                              setEditingNg(keyword);
                              setNewNgKeyword({
                                keyword: keyword.keyword,
                                description: keyword.description || '',
                                isActive: keyword.isActive,
                              });
                              setShowNgModal(true);
                            }}
                            className="p-1 text-blue-600 hover:bg-blue-50 rounded"
                          >
                            <Edit size={16} />
                          </button>
                          <button
                            onClick={() => deleteNgKeyword(keyword.id)}
                            className="p-1 text-red-600 hover:bg-red-50 rounded"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* 文言置換ルールタブ */}
      {activeTab === 'replacement-rules' && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <p className="text-sm text-gray-600">
              商品名や説明文から不要な文言を自動削除・置換します
            </p>
            <button
              onClick={() => {
                setEditingRule(null);
                setNewRule({ searchText: '', replaceText: '', description: '', isRegex: false, isActive: true });
                setShowRuleModal(true);
              }}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              <Plus size={16} />
              置換ルール追加
            </button>
          </div>

          <div className="bg-white rounded-lg shadow overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">検索文字列</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">置換後</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">説明</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">タイプ</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">ステータス</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">操作</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-8 text-center">
                      <RefreshCw size={24} className="animate-spin mx-auto mb-2" />
                      読み込み中...
                    </td>
                  </tr>
                ) : replacementRules.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-8 text-center text-gray-500">
                      置換ルールが登録されていません
                    </td>
                  </tr>
                ) : (
                  replacementRules.map(rule => (
                    <tr key={rule.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <code className="bg-gray-100 px-2 py-1 rounded text-sm">{rule.searchText}</code>
                      </td>
                      <td className="px-4 py-3">
                        <code className="bg-blue-50 px-2 py-1 rounded text-sm">{rule.replaceText || '(削除)'}</code>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {rule.description || '-'}
                      </td>
                      <td className="px-4 py-3">
                        {rule.isRegex ? (
                          <span className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded">正規表現</span>
                        ) : (
                          <span className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded">通常</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {rule.isActive ? (
                          <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs">
                            <Check size={12} />
                            有効
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-600 rounded-full text-xs">
                            <XCircle size={12} />
                            無効
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => {
                              setEditingRule(rule);
                              setNewRule({
                                searchText: rule.searchText,
                                replaceText: rule.replaceText,
                                description: rule.description || '',
                                isRegex: rule.isRegex,
                                isActive: rule.isActive,
                              });
                              setShowRuleModal(true);
                            }}
                            className="p-1 text-blue-600 hover:bg-blue-50 rounded"
                          >
                            <Edit size={16} />
                          </button>
                          <button
                            onClick={() => deleteReplacementRule(rule.id)}
                            className="p-1 text-red-600 hover:bg-red-50 rounded"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* フィルタテストタブ */}
      {activeTab === 'test' && (
        <div className="space-y-6">
          <div className="bg-white rounded-lg shadow p-6 space-y-4">
            <h3 className="font-medium text-lg">フィルタテスト</h3>
            <p className="text-sm text-gray-600">
              商品名や説明文を入力して、NGキーワードチェックと文言置換をテストできます
            </p>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">テスト用テキスト</label>
              <textarea
                value={testText}
                onChange={(e) => setTestText(e.target.value)}
                placeholder="例: 【Amazon.co.jp限定】サンプル商品 CELLWORLD製"
                className="w-full border rounded-lg px-3 py-2 h-32"
              />
            </div>
            <button
              onClick={runTest}
              disabled={loading || !testText}
              className="flex items-center gap-2 px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
            >
              <Search size={16} />
              テスト実行
            </button>
          </div>

          {testResult && (
            <div className="bg-white rounded-lg shadow p-6 space-y-4">
              <h3 className="font-medium text-lg">テスト結果</h3>
              
              {testResult.isFiltered ? (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 text-red-700 font-medium mb-2">
                    <XCircle size={20} />
                    NGフィルタに引っかかりました
                  </div>
                  <div className="text-sm text-red-600">{testResult.filterReason}</div>
                </div>
              ) : (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 text-green-700 font-medium mb-2">
                    <Check size={20} />
                    NGフィルタを通過しました
                  </div>
                </div>
              )}

              {testResult.originalTitle && (
                <div className="space-y-2">
                  <div>
                    <div className="text-sm font-medium text-gray-500 mb-1">元のテキスト:</div>
                    <div className="bg-gray-100 p-3 rounded">{testResult.originalTitle}</div>
                  </div>
                  <div>
                    <div className="text-sm font-medium text-gray-500 mb-1">置換後:</div>
                    <div className="bg-blue-50 p-3 rounded font-medium">{testResult.title}</div>
                  </div>
                </div>
              )}

              {!testResult.originalTitle && !testResult.isFiltered && (
                <div className="text-sm text-gray-600">
                  置換ルールは適用されませんでした（元のテキストがそのまま使用されます）
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* NGキーワード追加・編集モーダル */}
      {showNgModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg p-6">
            <h3 className="text-lg font-bold mb-4">
              {editingNg ? 'NGキーワード編集' : 'NGキーワード追加'}
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">キーワード *</label>
                <input
                  type="text"
                  value={newNgKeyword.keyword}
                  onChange={(e) => setNewNgKeyword({ ...newNgKeyword, keyword: e.target.value })}
                  placeholder="例: Amazon.co.jp限定"
                  className="w-full border rounded-lg px-3 py-2"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">説明（任意）</label>
                <input
                  type="text"
                  value={newNgKeyword.description}
                  onChange={(e) => setNewNgKeyword({ ...newNgKeyword, description: e.target.value })}
                  placeholder="例: Amazon限定商品を除外"
                  className="w-full border rounded-lg px-3 py-2"
                />
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={newNgKeyword.isActive}
                  onChange={(e) => setNewNgKeyword({ ...newNgKeyword, isActive: e.target.checked })}
                  className="rounded"
                />
                <label className="text-sm text-gray-700">有効にする</label>
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setShowNgModal(false)}
                className="px-4 py-2 border rounded-lg hover:bg-gray-50"
              >
                キャンセル
              </button>
              <button
                onClick={saveNgKeyword}
                disabled={loading}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                {editingNg ? '更新' : '登録'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 置換ルール追加・編集モーダル */}
      {showRuleModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg p-6">
            <h3 className="text-lg font-bold mb-4">
              {editingRule ? '置換ルール編集' : '置換ルール追加'}
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">検索文字列 *</label>
                <input
                  type="text"
                  value={newRule.searchText}
                  onChange={(e) => setNewRule({ ...newRule, searchText: e.target.value })}
                  placeholder="例: 【Amazon.co.jp限定】"
                  className="w-full border rounded-lg px-3 py-2"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">置換後の文字列</label>
                <input
                  type="text"
                  value={newRule.replaceText}
                  onChange={(e) => setNewRule({ ...newRule, replaceText: e.target.value })}
                  placeholder="空欄にすると削除されます"
                  className="w-full border rounded-lg px-3 py-2"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">説明（任意）</label>
                <input
                  type="text"
                  value={newRule.description}
                  onChange={(e) => setNewRule({ ...newRule, description: e.target.value })}
                  placeholder="例: Amazon限定表記を削除"
                  className="w-full border rounded-lg px-3 py-2"
                />
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={newRule.isRegex}
                  onChange={(e) => setNewRule({ ...newRule, isRegex: e.target.checked })}
                  className="rounded"
                />
                <label className="text-sm text-gray-700">正規表現を使用する</label>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={newRule.isActive}
                  onChange={(e) => setNewRule({ ...newRule, isActive: e.target.checked })}
                  className="rounded"
                />
                <label className="text-sm text-gray-700">有効にする</label>
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setShowRuleModal(false)}
                className="px-4 py-2 border rounded-lg hover:bg-gray-50"
              >
                キャンセル
              </button>
              <button
                onClick={saveReplacementRule}
                disabled={loading}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {editingRule ? '更新' : '登録'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

